import React, { useMemo } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { TanStackTable } from '@/@core/components/mui/TanStackTable'
import { TablePagination, TablePaginationProps } from '@/@core/components/mui/TablePagination'
import { useRouteQuery, useTranslator } from '@/hooks'
import { UserAvatar } from '../common/UserAvatar'
import TransactionStatusChip from './TransactionStatusChip'
import { ITransactionEmployeeDetail, TransactionStatusEnum } from '@/@data/transactions'
import { useRowSelection } from '@/@core/hooks'
import Link from '@/components/Link'
import dayjs from 'dayjs'
import { PaymentPartnerEnum } from '@/@data/company'

interface TransactionDetailTableTransactionProps {
  data: ITransactionEmployeeDetail[]
  meta?: TablePaginationProps | unknown
  isTransaction: boolean
  columnVisibility?: Record<string, boolean>
}

export const TransactionDetailTableTransaction: React.FC<TransactionDetailTableTransactionProps> = ({
  data,
  meta,
  isTransaction,
  columnVisibility = {}
}) => {
  const { __, __cc } = useTranslator()
  const { searchQuery } = useRouteQuery()

  const { rowSelection, setRowSelection } = useRowSelection(data)
  const getPartnerName = (paymentPartner?: string) => {
    if (!paymentPartner) {
      return ''
    }

    if (paymentPartner === PaymentPartnerEnum.Shinhan) {
      return 'Shinhan'
    }

    if (paymentPartner === PaymentPartnerEnum.VnptEpay) {
      return 'VNPT ePay'
    }

    return paymentPartner
  }

  const getTransactionPartnerLabel = (paymentPartner?: string, partnerCode?: string) => {
    if (!partnerCode) {
      return '-'
    }

    const partnerName = getPartnerName(paymentPartner)

    return partnerName ? `${partnerName} - ${partnerCode}` : partnerCode
  }

  const allColumns = useMemo<ColumnDef<ITransactionEmployeeDetail>[]>(
    () => [
      ...(isTransaction
        ? [
            {
              accessorKey: 'name',
              id: 'employees',
              header: __('Employees'),
              size: 260,
              cell: ({ row }: { row: { original: ITransactionEmployeeDetail } }) => (
                <UserAvatar user={row?.original?.user} link={`/cms/finance/transaction/${row?.original?.company_id}/employee/${row.original?.id}`} />
              )
            }
          ]
        : []),
      {
        accessorKey: 'request_transaction',
        id: 'request_transaction',
        header: __('Request Transaction'),
        size: 250,
        cell: ({ row }) =>
          isTransaction ? (
            <Link
              className='text-[#3551F2]'
              href={`/cms/finance/transaction/${row?.original?.company_id}/employee/${row.original?.id} `}
            >
              <div className='text-[#3551F2] text-[15px] font-normal'>
                {row?.original?.code || '-'}
              </div>
              <div className='text-[12px] font-normal text-[#2f2b3d66]'>
                {getTransactionPartnerLabel(row?.original?.payment_partner, row?.original?.partner_code)}
              </div>
            </Link>
          ) : (
            <Link
              className='text-[#3551F2]'
              href={`/cms/finance/transaction/${row?.original?.company_id}/employee/${row.original?.id} `}
            >
              <div className='text-[#3551F2] text-[15px] font-normal'>
                {row?.original?.code || '-'}
              </div>
              <div className='text-[12px] font-normal text-[#2f2b3d66]'>
                {getTransactionPartnerLabel(row?.original?.payment_partner, row?.original?.partner_code)}
              </div>
            </Link>
          )
      },
      {
        accessorKey: 'create_date',
        id: 'create_date',
        header: __('Create Date Transaction'),
        size: 280,
        cell: ({ row }) => (
          <div className='text-[15px] font-normal text-[#2f2b3db3]'>
            {row?.original?.created_at ? dayjs(row?.original?.created_at).format('DD/MM/YYYY') : '-'}
          </div>
        )
      },
      {
        accessorKey: 'update_date',
        id: 'update_date',
        header: __('Update Date'),
        size: 160,
        cell: ({ row }) => (
          <div className='text-[15px] font-normal text-[#2f2b3db3]'>
            {row?.original?.updated_at ? dayjs(row?.original?.updated_at).format('DD/MM/YYYY') : '-'}
          </div>
        )
      },
      {
        accessorKey: 'status',
        id: 'status',
        header: __('Status Transaction'),
        size: 240,
        cell: ({ row }) => (
          <TransactionStatusChip
            status={row?.original?.status as TransactionStatusEnum}
            isTable={true}
            noteFailed={row?.original?.reason}
          />
        )
      },
      {
        accessorKey: 'amount',
        id: 'amount',
        header: __('Amount EWA Disbursement'),
        size: 280,
        cell: ({ row }) => (
          <div className='text-[15px] font-normal text-[#2f2b3de6]'>
            {__cc(row?.original?.request_amount)}
            <span className='text-[#2f2b3db3] ml-1'>₫</span>
          </div>
        )
      },
      {
        accessorKey: 'eligibility_rate',
        id: 'eligibility_rate',
        header: __('Eligibility rate'),
        size: 200,
        cell: ({ row }) => (
          <div className='text-[15px] font-normal text-[#2f2b3de6]'>
            {__cc(row?.original?.eligibility_rate)}
            <span className='text-[#2f2b3db3] ml-1'>%</span>
          </div>
        )
      },
      {
        accessorKey: 'eligibility_amount',
        id: 'eligibility_amount',
        header: __('Est. Max EWA Disbursement'),
        size: 300,
        cell: ({ row }) => (
          <div className='text-[15px] font-normal text-[#2f2b3de6]'>
            {__cc(row?.original?.eligibility_amount)}
            <span className='text-[#2f2b3db3] ml-1'>₫</span>
          </div>
        )
      },
      {
        accessorKey: 'service_fee',
        id: 'service_fee',
        header: __('Services Fee'),
        cell: ({ row }) => (
          <div className='text-[15px] font-normal text-[#2f2b3de6]'>
            {__cc(row?.original?.service_fee)}
            <span className='text-[#2f2b3db3] ml-1'>₫</span>
          </div>
        ),
        size: 220
      },
      {
        accessorKey: 'voucher_discount',
        id: 'voucher_discount',
        header: __('Voucher'),
        cell: ({ row }) => (
          <div className='text-[15px] font-normal text-[#2f2b3de6]'>
            {__cc(row?.original?.voucher_discount)}
            <span className='text-[#2f2b3db3] ml-1'>₫</span>
          </div>
        ),
        size: 220
      }
      // {
      //   id: 'action',
      //   header: __('Action'),
      //   cell: ({ row }) => (
      //     <Link
      //       href={`/finance/transaction/${row?.original.company_id}/employee/${row?.original.id}`}
      //       className='flex items-center gap-4'
      //     >
      //       <i className='tabler-edit' />
      //     </Link>
      //   )
      // }
    ],
    [__, isTransaction, __cc]
  )

  // Filter columns based on visibility
  const columns = useMemo(() => {
    return allColumns.filter(column => {
      const columnId = column.id || ((column as any).accessorKey as string)

      return columnVisibility[columnId] !== false
    })
  }, [allColumns, columnVisibility])

  return (
    <>
      <TanStackTable
        columns={columns}
        tableData={data}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        borderRight='2px solid #e0e0e0'
      />

      {/* {meta && <TablePagination meta={meta as TablePaginationProps} />} */}
      {meta && (
        <TablePagination
          meta={
            {
              ...meta,
              page: Number(searchQuery.get('page')) || 1
            } as TablePaginationProps
          }
        />
      )}
    </>
  )
}

export default TransactionDetailTableTransaction
